import { TableauChartConfigDto } from '@asset-metrix/portal-gateway-client-js';
import { isPureArray } from '@asset-metrix/portal-ui';
import dayjs from 'dayjs';

import { Filter, FilterData } from '../../store/states/filterTypes';

export function getTableauIframeUrl(
  token: string,
  config: TableauChartConfigDto,
  pageFilter: Filter = {}
) {
  const {
    configuredFilter,
    tableauServer,
    tableauSite,
    tableauWorkbook,
    tableauWorksheet,
  } = config;

  const url = new URL(
    `https://${tableauServer}/${token}/t/${tableauSite}/views/${tableauWorkbook}/${tableauWorksheet}`
  );

  url.search = new URLSearchParams(
    toTableauFilterFormat(
      assembleTableauUrlFilter(pageFilter, configuredFilter)
    )
  ).toString();

  return url.toString();
}

export function getTableauIdentifier(config: TableauChartConfigDto) {
  return `${config.tableauWorkbook}/${config.tableauWorksheet}`;
}

export function assembleTableauUrlFilter(
  pageFilter: Filter,
  configuredFilter?: TableauChartConfigDto['configuredFilter']
) {
  const tableauFilter: Record<string, FilterData['selection']> = {
    ...configuredFilter,
  };

  for (const [key, value] of Object.entries(pageFilter)) {
    const selection = value?.selection;
    if (selection == null) {
      continue;
    }

    // For a configured filter sharing the same key with a page filter we eventually need to prune page filter selection
    // This prevents filtering tableau charts by forbidden filter values
    if (key in tableauFilter) {
      const curTableauFilter = tableauFilter[key];
      // We only need to take care of configured filter with multiple values as a single value always takes precedence over page filters
      if (Array.isArray(curTableauFilter)) {
        // prune page filter selections by configured filter values
        if (Array.isArray(selection)) {
          const prunedSelection = selection.filter((val) =>
            (curTableauFilter as unknown[]).includes(val)
          );
          // only add pruned page filter selection if there is at least one valid value left
          if (prunedSelection.length > 0) {
            tableauFilter[key] = isPureArray(prunedSelection)
              ? prunedSelection
              : prunedSelection.map(String);
          }
        } else if (
          typeof selection === 'string' ||
          typeof selection === 'boolean'
        ) {
          if ((curTableauFilter as unknown[]).includes(selection)) {
            // add valid single value
            tableauFilter[key] = selection;
          }
        } else {
          const intersection = curTableauFilter.filter((date) => {
            if (date == null || !dayjs(String(date), 'YYYY-MM-DD').isValid()) {
              // Any values that are not in the correct date format should be kept
              // and not be affected by the user selected date range
              return true;
            }

            return (
              (selection.start == null || date >= selection.start) &&
              (selection.end == null || date <= selection.end)
            );
          });
          if (intersection.length > 0) {
            tableauFilter[key] = isPureArray(intersection)
              ? intersection
              : intersection.map(String);
          }
        }
      }
      continue;
    }

    // always append page filter selection if key isn't set as configured filter
    tableauFilter[key] = selection;
  }
  return tableauFilter;
}

export function toTableauFilterFormat(
  tableauFilter: Record<string, FilterData['selection']>
) {
  const tableauFilterParams: Record<string, string> = {};
  for (const [key, value] of Object.entries(tableauFilter)) {
    if (Array.isArray(value)) {
      tableauFilterParams[key] = value.filter((val) => val !== '').join(',');
      continue;
    }

    if (typeof value === 'string' || typeof value === 'boolean') {
      if (value !== '') {
        tableauFilterParams[key] = String(value);
      }
      continue;
    }

    if (value?.start) {
      tableauFilterParams[`${key}Min`] = value.start + '';
    }
    if (value?.end) {
      tableauFilterParams[`${key}Max`] = value.end + '';
    }
  }
  return tableauFilterParams;
}
